import { Link, Navigate, useParams } from "react-router-dom";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { siteConfig } from "../lib/siteConfig";

const updated = "August 7, 2026";

const pages = {
  about: {
    eyebrow: "Company",
    title: `About ${siteConfig.name}`,
    intro: `${siteConfig.name} is an online social-casino entertainment site built for adults who enjoy slots-style games, weekly leaderboards, rewards, and fast support.`,
    sections: [
      {
        title: "What we offer",
        body: [
          "Players can create an account, explore available games, request game accounts, load eligible wallet balance into supported games, and request withdrawals when platform rules are met.",
          "Our team reviews payments, withdrawals, account issues, support requests, and ID verification when required by site settings.",
        ],
      },
      {
        title: "Player-first support",
        body: [
          "Support is available from inside the website. Players can send messages and screenshots so our team can investigate account, payment, game-load, and withdrawal questions.",
          "We aim to keep records clear: approvals, rejections, reasons, and account actions should be visible to the player when they affect the account.",
        ],
      },
      {
        title: "18+ only",
        body: [
          `${siteConfig.name} is intended only for adults 18 years of age or older and only where this type of online social-casino/sweepstakes play is permitted.`,
        ],
      },
    ],
  },
  press: {
    eyebrow: "Company",
    title: "Press",
    intro: `For press, partnership, or brand questions about ${siteConfig.name}, contact our support team.`,
    sections: [
      {
        title: "Media inquiries",
        body: [
          `Email: ${siteConfig.supportEmail}`,
          "Please include your name, organization, deadline, and the reason for your request.",
        ],
      },
      {
        title: "Brand assets",
        body: [
          `Please do not use the ${siteConfig.name} name, logo, screenshots, or player-facing materials in a way that implies endorsement without written permission.`,
        ],
      },
    ],
  },
  careers: {
    eyebrow: "Company",
    title: "Careers",
    intro: `${siteConfig.name} is not listing public job openings at this time.`,
    sections: [
      {
        title: "Interested in helping?",
        body: [
          `You can contact ${siteConfig.supportEmail} with your name, experience, and the type of role you are interested in.`,
          "We review messages as needed and will respond if there is a fit.",
        ],
      },
    ],
  },
  terms: {
    eyebrow: "Legal",
    title: "Terms & Conditions",
    intro: `These Terms explain the rules for using ${siteConfig.name}. By creating an account or using the site, you agree to follow these Terms.`,
    sections: [
      {
        title: "1. Eligibility",
        body: [
          "You must be at least 18 years old to use this site.",
          "You may only use the site from places where this type of online social-casino or sweepstakes entertainment is legal. We may restrict, suspend, or close accounts where required by law, platform rules, risk controls, or provider requirements.",
        ],
      },
      {
        title: "2. Account responsibility",
        body: [
          "You are responsible for keeping your login information secure and for all activity under your account.",
          "You may not sell, share, transfer, or operate multiple accounts to avoid limits, verification, rules, or reward restrictions.",
        ],
      },
      {
        title: "3. Entertainment and sweepstakes-style rewards",
        body: [
          `${siteConfig.name} provides social-casino entertainment and reward features. Game availability, balances, bonuses, leaderboards, promotions, and redemption rules may change at any time.`,
          "Rewards, freeplay, wallet credits, bonuses, and redemption eligibility are subject to verification, limits, fraud checks, provider status, and admin approval.",
        ],
      },
      {
        title: "4. Payments, wallet, loads, and withdrawals",
        body: [
          "Add-money, game-load, redeem, and withdrawal requests may be reviewed before approval. Processing times can vary by payment method, provider availability, verification status, risk review, and support volume.",
          "Rejected or failed game-load attempts may be refunded to the wallet or freeplay balance according to the original funding source and site records.",
          "Withdrawal limits, minimums, maximums, fees, verification requirements, and payout methods are shown in the site or admin-approved settings and may be updated when needed.",
        ],
      },
      {
        title: "5. Verification and security review",
        body: [
          "We may require phone verification, ID/selfie verification, payment proof, account ownership checks, or other information before approving withdrawals, rewards, or account changes.",
          "We may reject verification when documents are unclear, information does not match, identity appears duplicated, the account violates rules, or provider/review systems return a risk flag.",
        ],
      },
      {
        title: "6. Prohibited activity",
        body: [
          "You may not use bots, automation, fake identities, stolen payment information, chargeback abuse, account sharing, duplicate accounts, fraud, harassment, security testing without permission, or attempts to bypass platform limits.",
          "Violations may result in cancelled rewards, withheld withdrawals, account suspension, account closure, or reporting to payment/provider partners when appropriate.",
        ],
      },
      {
        title: "7. Third-party providers",
        body: [
          "Some games, payment flows, identity checks, communications, and hosting features may be provided by third parties. Their systems may be unavailable, delayed, or return errors outside our direct control.",
          "We try to keep customer-facing records accurate, but provider balances and confirmations may require refresh or manual review.",
        ],
      },
      {
        title: "8. Changes to these Terms",
        body: [
          "We may update these Terms when the service, rules, providers, legal requirements, or risk controls change. Continued use of the site means you accept the updated Terms.",
        ],
      },
      {
        title: "9. Contact",
        body: [`Questions about these Terms can be sent to ${siteConfig.supportEmail}.`],
      },
    ],
  },
  privacy: {
    eyebrow: "Legal",
    title: "Privacy Policy",
    intro: `This Privacy Policy explains how ${siteConfig.name} collects, uses, stores, and shares information when you use the site.`,
    sections: [
      {
        title: "Information we collect",
        body: [
          "Account information such as username, display name, email, phone number, password hash, referral information, profile details, and account status.",
          "Payment and wallet records such as add-money requests, withdrawal requests, payout details you submit, transaction references, screenshots, approval/rejection notes, and support records.",
          "Verification information such as date of birth, state, phone verification status, ID/selfie submission status, Didit session information, rejection reasons, and manually uploaded verification images when used.",
          "Technical information such as device/browser data, IP-related security signals, cookies/local storage, error logs, and usage activity needed to run and protect the site.",
        ],
      },
      {
        title: "How we use information",
        body: [
          "To create and manage accounts, provide games and support, process payments and withdrawals, operate rewards, detect abuse, verify identity, troubleshoot provider issues, and keep records accurate.",
          "To send account-related messages, payment/withdrawal status updates, verification notices, security alerts, and support responses.",
        ],
      },
      {
        title: "Sharing information",
        body: [
          "We may share information with service providers that help us operate the site, including payment processors, identity verification providers, game/provider systems, hosting, analytics, SMS/email/Telegram tools, and support systems.",
          "We may disclose information when needed to comply with law, enforce rules, investigate fraud, protect players, or respond to valid legal/security requests.",
        ],
      },
      {
        title: "Security and retention",
        body: [
          "We use technical and administrative safeguards intended to protect account, payment, and verification records. No online system is completely risk-free.",
          "We keep records as long as needed for account operation, legal/compliance, fraud prevention, dispute handling, tax/accounting, security, and backup purposes.",
        ],
      },
      {
        title: "Your choices",
        body: [
          "You can update certain profile details in your account. For account, support, verification, or data questions, contact support.",
          "We may need to retain some records even after account closure where required for legal, fraud-prevention, payment, security, or dispute reasons.",
        ],
      },
      {
        title: "Contact",
        body: [`Privacy questions can be sent to ${siteConfig.supportEmail}.`],
      },
    ],
  },
  "responsible-play": {
    eyebrow: "Legal",
    title: "Responsible Play",
    intro: `${siteConfig.name} is for adults who want entertainment. Play should stay controlled, affordable, and recreational.`,
    sections: [
      {
        title: "Play within limits",
        body: [
          "Only play with time and money you can afford to spend. Do not use the site as a source of income or a way to solve financial problems.",
          "Take breaks, set personal limits, and stop if play no longer feels fun or controlled.",
        ],
      },
      {
        title: "18+ only",
        body: [
          "Do not allow minors to use your account, device, payment methods, or game credentials. Keep your password private and secure.",
        ],
      },
      {
        title: "When to pause",
        body: [
          "Consider taking a break if you are hiding play from others, chasing losses, borrowing money to play, feeling stressed about play, or struggling to stop.",
          "If you need help pausing or closing your account, contact support and ask for a cooling-off period or account restriction.",
        ],
      },
      {
        title: "Verification and withdrawal controls",
        body: [
          "Verification, withdrawal limits, fraud checks, and manual review are used to protect players, prevent abuse, and keep payouts safer.",
        ],
      },
      {
        title: "Need help?",
        body: [
          `Contact ${siteConfig.supportEmail} if you want account limits, a cooling-off period, or account closure.`,
          "If you feel at immediate risk or unable to control play, contact trusted local support services or emergency support in your area.",
        ],
      },
    ],
  },
};

export default function LegalPage() {
  const { slug = "about" } = useParams();
  const page = pages[slug];
  if (!page) return <Navigate to="/about" replace />;

  return (
    <div className="min-h-screen bg-slate-50 text-slate-950">
      <Navbar />
      <main className="mx-auto max-w-4xl px-6 pb-20 pt-16 md:px-10">
        <div className="mb-10">
          <div className="mb-4 text-xs font-black uppercase tracking-[0.24em] text-[#2F6FA5]">{page.eyebrow}</div>
          <h1 className="font-display text-4xl font-black tracking-tighter md:text-6xl">{page.title}</h1>
          <p className="mt-5 max-w-3xl text-lg leading-relaxed text-slate-600">{page.intro}</p>
          {["terms", "privacy", "responsible-play"].includes(slug) && (
            <p className="mt-3 text-sm text-slate-500">Last updated: {updated}</p>
          )}
        </div>

        <div className="space-y-5">
          {page.sections.map((section) => (
            <section key={section.title} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
              <h2 className="mb-3 text-xl font-bold">{section.title}</h2>
              <div className="space-y-3 text-sm leading-7 text-slate-600">
                {section.body.map((text) => (
                  <p key={text}>{text}</p>
                ))}
              </div>
            </section>
          ))}
        </div>

        {["terms", "privacy", "responsible-play"].includes(slug) && (
          <div className="mt-8 rounded-2xl border border-amber-200 bg-amber-50 p-5 text-sm leading-6 text-amber-900">
            These pages are provided for general site rules and customer transparency. They should be reviewed by qualified legal counsel for your exact business model, state availability, promotions, and payment/withdrawal practices.
          </div>
        )}

        <div className="mt-10">
          <Link to="/support" className="inline-flex rounded-full bg-[#2F6FA5] px-5 py-3 text-sm font-bold text-white hover:bg-[#4E91C8]">
            Contact support
          </Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
